/**
 * Hudson Valley Coffee Company
 * Theme JavaScript
 */

(function() {
  'use strict';

  /* ================================
     Mobile Navigation
     ================================ */
  const MobileNav = {
    init() {
      this.btn = document.querySelector('.header__mobile-menu-btn');
      this.nav = document.querySelector('.mobile-nav');
      
      if (!this.btn || !this.nav) return;
      
      this.btn.addEventListener('click', () => this.toggle());
      
      // Close menu when clicking nav links
      this.nav.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => this.close());
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    },

    toggle() {
      this.btn.classList.toggle('active');
      this.nav.classList.toggle('active');
      document.body.classList.toggle('mobile-nav-open');
    },

    close() {
      this.btn.classList.remove('active');
      this.nav.classList.remove('active');
      document.body.classList.remove('mobile-nav-open');
    },

    isOpen() {
      return this.nav.classList.contains('active');
    }
  };

  /* ================================
     Product Form
     ================================ */
  const ProductForm = {
    init() {
      this.wrappers = document.querySelectorAll('[data-product-form]');
      this.wrappers.forEach(wrapper => this.setupForm(wrapper));
    },

    setupForm(wrapper) {
      const form = wrapper.querySelector('form');
      if (!form) return;

      const purchaseOptions = wrapper.querySelectorAll('.purchase-option');
      const frequencySelector = wrapper.querySelector('.frequency-selector');
      const variantOptions = wrapper.querySelectorAll('.variant-option');
      const quantityControls = wrapper.querySelector('.quantity-controls');

      // Purchase option selection (one-time vs subscription)
      purchaseOptions.forEach(option => {
        option.addEventListener('click', () => {
          purchaseOptions.forEach(opt => opt.classList.remove('selected'));
          option.classList.add('selected');
          
          const type = option.dataset.purchaseType;
          if (frequencySelector) {
            frequencySelector.classList.toggle('visible', type === 'subscribe');
          }

          this.updatePriceDisplay(wrapper);
        });
      });

      // Variant option selection
      variantOptions.forEach(option => {
        option.addEventListener('click', () => {
          const group = option.closest('.product-form__variant-selector');
          group.querySelectorAll('.variant-option').forEach(opt => {
            opt.classList.remove('selected');
          });
          option.classList.add('selected');
          
          this.updateSelectedVariant(wrapper, form);
        });
      });

      // Quantity controls
      if (quantityControls) {
        const minusBtn = quantityControls.querySelector('[data-qty-minus]');
        const plusBtn = quantityControls.querySelector('[data-qty-plus]');
        const input = quantityControls.querySelector('input');

        if (minusBtn) {
          minusBtn.addEventListener('click', () => {
            const value = parseInt(input.value);
            if (value > 1) input.value = value - 1;
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => {
            const value = parseInt(input.value);
            input.value = value + 1;
          });
        }
      }

      // Form submission
      form.addEventListener('submit', (e) => this.handleSubmit(e, wrapper));
      
      // Initial price display update
      this.updatePriceDisplay(wrapper);
    },

    updatePriceDisplay(wrapper) {
      const priceDisplay = wrapper.querySelector('[data-price-display]');
      if (!priceDisplay) return;

      const basePrice = parseFloat(wrapper.dataset.productPrice);
      const selectedOption = wrapper.querySelector('.purchase-option.selected');
      
      // Check if subscription is selected
      const isSubscription = selectedOption && selectedOption.dataset.purchaseType === 'subscribe';
      
      if (isSubscription && window.theme && window.theme.settings && window.theme.settings.subscriptionsEnabled) {
        const discount = window.theme.settings.subscriptionDiscountValue;
        const discountType = window.theme.settings.subscriptionDiscountType;
        
        let finalPrice;
        if (discountType === 'percentage') {
          finalPrice = basePrice * (1 - discount / 100);
        } else {
          finalPrice = basePrice - discount;
        }
        
        priceDisplay.innerHTML = `
          <span class="price">
            <span class="price__compare">${this.formatMoney(basePrice)}</span>
            <span class="price__current">${this.formatMoney(finalPrice)}</span>
          </span>
        `;
      } else {
        priceDisplay.innerHTML = `
          <span class="price">
            <span class="price__current">${this.formatMoney(basePrice)}</span>
          </span>
        `;
      }
      
      // Also update the purchase option prices if they exist
      const onetimePrice = wrapper.querySelector('[data-purchase-type="onetime"] .purchase-option__price');
      if (onetimePrice) {
        onetimePrice.textContent = this.formatMoney(basePrice);
      }
      
      const subscribePrice = wrapper.querySelector('[data-purchase-type="subscribe"] .purchase-option__price');
      if (subscribePrice && window.theme && window.theme.settings) {
        const discount = window.theme.settings.subscriptionDiscountValue;
        const discountType = window.theme.settings.subscriptionDiscountType;
        let subPrice;
        if (discountType === 'percentage') {
          subPrice = basePrice * (1 - discount / 100);
        } else {
          subPrice = basePrice - discount;
        }
        subscribePrice.textContent = this.formatMoney(subPrice);
      }
    },

    updateSelectedVariant(wrapper, form) {
      const selectedOptions = [];
      wrapper.querySelectorAll('.product-form__variant-selector').forEach(selector => {
        const selected = selector.querySelector('.variant-option.selected');
        if (selected) {
          selectedOptions.push(selected.dataset.value);
        }
      });

      // Find matching variant - read from script tag
      const variantsScript = wrapper.querySelector('[data-product-variants-json]');
      const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      const variant = variants.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (variant) {
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
        }

        // Update price if variant has different price (keep in cents)
        wrapper.dataset.productPrice = variant.price;
        this.updatePriceDisplay(wrapper);
      }
    },

    async handleSubmit(e, wrapper) {
      e.preventDefault();
      
      const form = wrapper.querySelector('form');
      const submitBtn = wrapper.querySelector('[type="submit"]');
      const originalText = submitBtn.textContent;
      submitBtn.textContent = 'Adding...';
      submitBtn.disabled = true;

      const formData = new FormData(form);
      const data = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity')) || 1
      };

      // Add subscription properties if applicable
      const selectedOption = wrapper.querySelector('.purchase-option.selected');
      if (selectedOption && selectedOption.dataset.purchaseType === 'subscribe') {
        const frequencySelect = wrapper.querySelector('.frequency-selector select');
        if (frequencySelect) {
          data.selling_plan = frequencySelect.value;
        }
      }

      try {
        const response = await fetch(window.theme.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (response.ok) {
          const item = await response.json();
          Cart.updateCount();
          
          if (window.theme.settings.cartType === 'drawer') {
            CartDrawer.open();
          } else {
            window.location.href = window.theme.routes.cart_url;
          }
        } else {
          const error = await response.json();
          alert(error.message || 'Error adding to cart');
        }
      } catch (err) {
        console.error('Add to cart error:', err);
        alert('Error adding to cart');
      } finally {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
    },

    formatMoney(cents) {
      cents = parseInt(cents) || 0;
      return '$' + (cents / 100).toFixed(2);
    }
  };

  /* ================================
     Cart
     ================================ */
  const Cart = {
    async updateCount() {
      try {
        const response = await fetch(window.theme.routes.cart_url + '.js');
        const cart = await response.json();
        
        document.querySelectorAll('.header__cart-count').forEach(el => {
          el.textContent = cart.item_count;
        });
      } catch (err) {
        console.error('Error updating cart count:', err);
      }
    }
  };

  /* ================================
     Cart Drawer
     ================================ */
  const CartDrawer = {
    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('.cart-drawer__overlay');
      this.closeBtn = document.querySelector('.cart-drawer__close');
      this.content = document.querySelector('[data-cart-drawer-content]');
      
      if (!this.drawer) return;

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    },

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('active');
      document.body.classList.add('cart-drawer-open');
      this.loadContents();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('active');
      document.body.classList.remove('cart-drawer-open');
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('active');
    },

    async loadContents() {
      if (!this.content) return;

      try {
        const response = await fetch('/?section_id=cart-drawer-items');
        const html = await response.text();
        
        // Parse the response to extract just the section content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const sectionContent = doc.querySelector('.shopify-section') || doc.body;
        
        this.content.innerHTML = sectionContent.innerHTML;
        this.bindItemEvents();
      } catch (err) {
        console.error('Error loading cart:', err);
        this.content.innerHTML = '<p class="cart-drawer__empty">Error loading cart</p>';
      }
    },

    bindItemEvents() {
      // Quantity changes
      this.content.querySelectorAll('[data-cart-qty]').forEach(input => {
        input.addEventListener('change', async (e) => {
          const key = e.target.dataset.cartKey;
          const qty = parseInt(e.target.value);
          await this.updateItem(key, qty);
        });
      });

      // Remove buttons
      this.content.querySelectorAll('[data-cart-remove]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const key = e.target.dataset.cartKey;
          await this.updateItem(key, 0);
        });
      });
      
      // Continue shopping link - close drawer first
      this.content.querySelectorAll('[data-close-drawer]').forEach(link => {
        link.addEventListener('click', (e) => {
          this.close();
        });
      });
    },

    async updateItem(key, quantity) {
      try {
        const response = await fetch(window.theme.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });

        if (response.ok) {
          Cart.updateCount();
          this.loadContents();
        }
      } catch (err) {
        console.error('Error updating cart:', err);
      }
    }
  };

  /* ================================
     Wholesale Pricing
     ================================ */
  const WholesalePricing = {
    init() {
      if (!window.theme || !window.theme.settings || !window.theme.settings.wholesaleEnabled) return;
      
      const customerTags = (window.theme.customer && window.theme.customer.tags) || [];
      const wholesaleTag = window.theme.settings.wholesaleCustomerTag;
      
      this.isWholesale = customerTags.includes(wholesaleTag);
      
      if (this.isWholesale) {
        document.body.classList.add('is-wholesale-customer');
        this.updatePrices();
      }
    },

    updatePrices() {
      document.querySelectorAll('[data-wholesale-price]').forEach(el => {
        el.style.display = 'block';
      });
      
      document.querySelectorAll('[data-retail-price]').forEach(el => {
        el.classList.add('compare-price');
      });
    }
  };

  /* ================================
     Newsletter Form
     ================================ */
  const NewsletterForm = {
    init() {
      this.forms = document.querySelectorAll('.newsletter__form');
      this.forms.forEach(form => {
        form.addEventListener('submit', (e) => this.handleSubmit(e, form));
      });
    },

    async handleSubmit(e, form) {
      e.preventDefault();
      
      const email = form.querySelector('input[type="email"]').value;
      const btn = form.querySelector('button');
      const originalText = btn.textContent;
      
      btn.textContent = 'Subscribing...';
      btn.disabled = true;

      // Note: This would need to be connected to your actual newsletter service
      // This is a placeholder that simulates success
      setTimeout(() => {
        btn.textContent = 'Subscribed!';
        form.querySelector('input[type="email"]').value = '';
        
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2000);
      }, 1000);
    }
  };

  /* ================================
     Image Gallery
     ================================ */
  const ProductGallery = {
    init() {
      this.galleries = document.querySelectorAll('[data-product-gallery]');
      this.galleries.forEach(gallery => this.setupGallery(gallery));
    },

    setupGallery(gallery) {
      const mainImage = gallery.querySelector('[data-main-image]');
      const thumbnails = gallery.querySelectorAll('[data-thumbnail]');

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          
          const imgSrc = thumb.dataset.imageSrc;
          const imgAlt = thumb.dataset.imageAlt;
          
          if (mainImage) {
            mainImage.src = imgSrc;
            mainImage.alt = imgAlt;
          }
        });
      });
    }
  };

  /* ================================
     Announcement Bar
     ================================ */
  const AnnouncementBar = {
    init() {
      this.bar = document.querySelector('.announcement-bar');
      this.closeBtn = document.querySelector('.announcement-bar__close');
      
      if (!this.closeBtn) return;

      this.closeBtn.addEventListener('click', () => {
        this.bar.style.display = 'none';
        sessionStorage.setItem('announcement-closed', 'true');
      });

      // Check if already closed this session
      if (sessionStorage.getItem('announcement-closed') === 'true') {
        this.bar.style.display = 'none';
      }
    }
  };

  /* ================================
     Initialize
     ================================ */
  document.addEventListener('DOMContentLoaded', () => {
    MobileNav.init();
    ProductForm.init();
    CartDrawer.init();
    WholesalePricing.init();
    NewsletterForm.init();
    ProductGallery.init();
    AnnouncementBar.init();
  });

  // Expose to global scope for external access
  window.HVCCTheme = {
    MobileNav,
    Cart,
    CartDrawer,
    ProductForm
  };

})();